import { useState, useEffect, useCallback } from 'react';
import { Vendor, VendorListResponse, VendorCreate, VendorUpdate } from '../types/costwand.d';
import { apiClient, ApiError } from '../api/apiClient.ts';

const PAGE_SIZE = 10;

interface UseVendorsResult {
  vendors: Vendor[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchVendors: (page: number) => Promise<void>;
  handlePageChange: (page: number) => void;
  createVendor: (data: VendorCreate) => Promise<boolean>;
  updateVendor: (id: number, data: VendorUpdate) => Promise<boolean>;
  deleteVendor: (id: number) => Promise<boolean>;
}

export const useVendors = (): UseVendorsResult => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVendors = useCallback(async (page: number) => {
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<VendorListResponse>(
        `/vendors?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true }
      );
      setVendors(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch vendors: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching vendors.');
      }
      setVendors([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVendors(currentPage);
  }, [currentPage, fetchVendors]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createVendor = async (data: VendorCreate): Promise<boolean> => {
    setError(null);
    try {
      // Destructure to omit state_id
      const { state_id, ...payload } = data;
      await apiClient<Vendor>('/vendors', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      await fetchVendors(1); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      return false;
    }
  };

  const updateVendor = async (id: number, data: VendorUpdate): Promise<boolean> => {
    setError(null);
    try {
      // Destructure to omit state_id
      const { state_id, ...payload } = data;
      await apiClient<Vendor>(`/vendors/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      await fetchVendors(currentPage); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      return false;
    }
  };

  const deleteVendor = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Vendor ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/vendors/${id}`, { method: 'DELETE' });
      const pageToFetch = vendors.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchVendors(pageToFetch); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      return false;
    }
  };

  return {
    vendors,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchVendors,
    handlePageChange,
    createVendor,
    updateVendor,
    deleteVendor,
  };
};